<template>
  <div class="day-book">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter acc-select">
          <label>Voucher type</label>
          <FormDropdown v-model="filters.type" :options="typeOptions" placeholder="All voucher types" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Voucher, account, party or narration" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-files"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Vouchers</div>
          <div class="rp-card-value">{{ formatInt(summary.voucher_count) }}</div>
          <div class="rp-card-sub">{{ plural(summary.line_count, 'posting') }} over {{ plural(summary.day_count, 'day') }}</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-arrow-down-left"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Total Debit</div>
          <div class="rp-card-value">{{ formatMoney(summary.debit) }}</div>
          <div class="rp-card-sub">of the vouchers shown</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-arrow-up-right"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Total Credit</div>
          <div class="rp-card-value">{{ formatMoney(summary.credit) }}</div>
          <div class="rp-card-sub">of the vouchers shown</div>
        </div>
      </div>
      <div class="rp-card" :class="summary.balanced ? 'tone-teal' : 'tone-red'">
        <span class="rp-card-icon"><i class="ti" :class="summary.balanced ? 'ti-circle-check' : 'ti-alert-triangle'"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Debit = Credit</div>
          <div class="rp-card-value" :class="summary.balanced ? 'text-success' : 'text-danger'">
            {{ summary.balanced ? 'Balanced' : formatMoney(summary.difference) }}
          </div>
          <div class="rp-card-sub">{{ summary.balanced ? 'every voucher adds up' : 'difference between debit and credit' }}</div>
        </div>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button type="button" class="rp-pill" :class="{ active: !filters.type }" @click="filters.type = null">
            All <span class="rp-pill-count">{{ formatInt(summary.all_vouchers) }}</span>
          </button>
          <button v-for="entry in summary.type_counts" :key="entry.key" type="button" class="rp-pill"
                  :class="{ active: filters.type === entry.key }" @click="filters.type = entry.key">
            {{ entry.label }} <span class="rp-pill-count">{{ formatInt(entry.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          lines per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher</th>
              <th>Account</th>
              <th>Party</th>
              <th>Narration</th>
              <th class="text-end">Debit</th>
              <th class="text-end">Credit</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in pagedRows" :key="row.key" :class="{ 'acc-voucher-start': row.first && index }">
              <td class="text-nowrap">{{ showVoucher(row, index) ? formatDate(row.date) : '' }}</td>
              <td>
                <template v-if="showVoucher(row, index)">
                  <div class="fw-medium text-nowrap">{{ row.voucher_id }}</div>
                  <span class="rp-badge" :class="typeTone(row.type_key)">{{ row.type }}</span>
                </template>
              </td>
              <td :style="{ paddingLeft: Number(row.credit) && !Number(row.debit) ? '2rem' : null }">
                <button type="button" class="acc-link" title="Open this account's ledger" @click="openLedger(row.account_id)">
                  {{ row.account }}
                </button>
                <span class="acc-code ms-1">{{ row.account_id }}</span>
              </td>
              <td>{{ row.party || '—' }}</td>
              <td><span class="rp-narration" :title="row.narration">{{ row.narration || '—' }}</span></td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.debit) }">{{ amountText(row.debit) }}</td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.credit) }">{{ amountText(row.credit) }}</td>
            </tr>
            <tr v-if="!loading && !pagedRows.length">
              <td colspan="7">
                <div class="rp-empty">
                  <i class="ti ti-notebook"></i>
                  <div class="fw-semibold">No vouchers found</div>
                  <small class="text-muted">Try another period, voucher type or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th colspan="5" class="text-end">
                Total ({{ plural(summary.voucher_count, 'voucher') }} · {{ plural(summary.line_count, 'line') }})
              </th>
              <th class="text-end">{{ formatMoney(summary.debit) }}</th>
              <th class="text-end">{{ formatMoney(summary.credit) }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="rows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="rows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Every approved posting of {{ options.store || 'the store' }}, voucher by voucher (debits first, credits indented). The voucher type
      comes from the voucher number; a search keeps the whole voucher. Click an account to open its ledger.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import accountingReport from './accountingReportMixin';

const EMPTY_SUMMARY = {
  voucher_count: 0, line_count: 0, debit: 0, credit: 0, difference: 0, balanced: true, day_count: 0, type_counts: [],
  all_vouchers: 0,
};

export default {
  name: 'DayBookReport',
  mixins: [inventoryReport, periodReport, accountingReport],

  reportUrl: apiUrls.day_book_report.get,
  fileName: 'day_book',
  columns: [],

  data() {
    return {
      filters: { ...defaultPeriod(), type: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
    };
  },

  computed: {
    typeOptions() {
      return (this.options.voucher_types || []).map((t) => ({ id: t.id, text: t.name }));
    },

    hasActiveFilters() {
      return this.periodChanged || !!(this.filters.type || this.filters.search.trim());
    },

    visibleRows() {
      return this.rows;
    },
  },

  watch: {
    'filters.type'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...(this.filters.type ? { type: this.filters.type } : {}) };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), type: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    /** The voucher's date / number show on its first line, and again at the top of a page. */
    showVoucher(row, index) {
      return row.first || index === 0;
    },

    printReport() {
      const s = this.summary;
      const search = this.filters.search.trim();
      const type = this.optionName(this.options.voucher_types || [], this.filters.type, 'All');
      this.printStatement(
        'Day Book',
        [this.periodText(), `Voucher type: ${type}`, this.storeLabel(), search && `Search: ${search}`],
        [['Vouchers', this.formatInt(s.voucher_count)], ['Debit', this.formatMoney(s.debit)],
          ['Credit', this.formatMoney(s.credit)], ['Status', s.balanced ? 'Balanced' : this.formatMoney(s.difference)]],
        [
          { label: 'Date', value: (r) => (r.first ? this.formatDate(r.date) : '') },
          { label: 'Voucher', value: (r) => (r.first ? r.voucher_id : '') },
          { label: 'Type', value: (r) => (r.first ? r.type : '') },
          { label: 'Account', value: (r) => `${r.account} (${r.account_id})` },
          { label: 'Party', value: (r) => r.party },
          { label: 'Narration', value: (r) => r.narration },
          { label: 'Debit', value: (r) => this.amountText(r.debit), num: true },
          { label: 'Credit', value: (r) => this.amountText(r.credit), num: true },
        ],
        this.rows,
        { Account: `${s.voucher_count} vouchers · ${s.line_count} lines`, Debit: this.formatMoney(s.debit), Credit: this.formatMoney(s.credit) },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
